import React, { useState, useEffect } from 'react';
import {
  Box, Container, Card, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button,
  Dialog, TextField, CircularProgress, Typography, DialogTitle, DialogContent, DialogActions, Chip,
  Snackbar, Alert, Grid, FormControl, InputLabel, Select, MenuItem, Rating,
} from '@mui/material';
import { campaignRatingService } from '../services/api';
import { Edit, Search, Star, CheckCircle, Schedule, Delete } from '@mui/icons-material';

const CampaignRatingsPage = () => {
  const [ratings, setRatings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [selected, setSelected] = useState(null);
  const [newStatus, setNewStatus] = useState('pending');
  const [adminNotes, setAdminNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({ open: false, message: '', type: 'success' });
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('created_at-desc');
  const [pageNumber, setPageNumber] = useState(1);
  const [counts, setCounts] = useState({ all: 0, pending: 0, approved: 0, rejected: 0 });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
  });

  useEffect(() => {
    fetchRatings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, filterStatus, sortBy, pageNumber]);

  const fetchRatings = async () => {
    try {
      setLoading(true);
      const response = await campaignRatingService.getAll({
        page: pageNumber,
        limit: 10,
        search: searchQuery,
        sort: sortBy,
        status: filterStatus,
      });

      let data = [];
      let paginationData = {
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: 10,
      };

      if (response.data?.data && Array.isArray(response.data.data)) {
        data = response.data.data;
        paginationData = response.data.pagination || paginationData;
      } else if (Array.isArray(response.data)) {
        data = response.data;
      }

      setRatings(data);
      setPagination(paginationData);
      if (response.data?.counts) setCounts(response.data.counts);
    } catch (error) {
      console.error('Error fetching ratings:', error);
      setRatings([]);
      setToast({ open: true, message: 'Error loading ratings', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = (row) => {
    setSelected(row);
    setNewStatus(row.status || 'pending');
    setAdminNotes(row.admin_notes || '');
    setOpenDialog(true);
  };

  const handleSaveStatus = async () => {
    try {
      setSaving(true);
      await campaignRatingService.updateStatus(selected.id, {
        status: newStatus,
        admin_notes: adminNotes,
      });
      setToast({ open: true, message: 'Rating status updated', type: 'success' });
      setOpenDialog(false);
      await fetchRatings();
    } catch (error) {
      setToast({
        open: true,
        message: 'Error updating status: ' + (error.response?.data?.error || error.message),
        type: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Delete rating from ${row.full_name}? This cannot be undone.`)) return;
    try {
      await campaignRatingService.delete(row.id);
      setToast({ open: true, message: 'Rating deleted', type: 'success' });
      await fetchRatings();
    } catch (error) {
      setToast({
        open: true,
        message: 'Error deleting rating: ' + (error.response?.data?.error || error.message),
        type: 'error',
      });
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'success';
      case 'rejected': return 'error';
      case 'pending': return 'warning';
      default: return 'default';
    }
  };

  const pageButtons = Math.max(1, pagination.totalPages || 1);

  return (
    <Box sx={{ backgroundColor: '#f5f7fa', minHeight: '100%', p: 4 }}>
      <Container maxWidth={false} sx={{ maxWidth: '100%' }}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1a202c', mb: 1 }}>
            Campaign Ratings
          </Typography>
          <Typography variant="body2" sx={{ color: '#667085' }}>
            Review user ratings. Approved reviews appear on the public campaign page.
          </Typography>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 3, mb: 4 }}>
          <Card sx={{ p: 3, border: '1px solid #e8ecf1', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ backgroundColor: '#dcfce7', p: 2, borderRadius: '12px' }}>
                <Star sx={{ color: '#2c5f2d', fontSize: 28 }} />
              </Box>
              <Box>
                <Typography variant="body2" sx={{ color: '#667085', fontWeight: 500 }}>Total ratings</Typography>
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#2c5f2d' }}>{counts.all}</Typography>
              </Box>
            </Box>
          </Card>
          <Card sx={{ p: 3, border: '1px solid #e8ecf1', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ backgroundColor: '#dcfce7', p: 2, borderRadius: '12px' }}>
                <CheckCircle sx={{ color: '#059669', fontSize: 28 }} />
              </Box>
              <Box>
                <Typography variant="body2" sx={{ color: '#667085', fontWeight: 500 }}>Approved</Typography>
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#059669' }}>{counts.approved}</Typography>
              </Box>
            </Box>
          </Card>
          <Card sx={{ p: 3, border: '1px solid #e8ecf1', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ backgroundColor: '#fef3c7', p: 2, borderRadius: '12px' }}>
                <Schedule sx={{ color: '#d97706', fontSize: 28 }} />
              </Box>
              <Box>
                <Typography variant="body2" sx={{ color: '#667085', fontWeight: 500 }}>Pending</Typography>
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#d97706' }}>{counts.pending}</Typography>
              </Box>
            </Box>
          </Card>
        </Box>

        <Card sx={{ p: 3, mb: 3, border: '1px solid #e8ecf1', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                placeholder="Search name, email, campaign..."
                variant="outlined"
                size="small"
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setPageNumber(1); }}
                InputProps={{ startAdornment: <Search sx={{ mr: 1, color: '#667085' }} /> }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={filterStatus}
                  label="Status"
                  onChange={(e) => { setFilterStatus(e.target.value); setPageNumber(1); }}
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="approved">Approved</MenuItem>
                  <MenuItem value="rejected">Rejected</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={sortBy}
                  label="Sort By"
                  onChange={(e) => { setSortBy(e.target.value); setPageNumber(1); }}
                >
                  <MenuItem value="created_at-desc">Date (Newest)</MenuItem>
                  <MenuItem value="created_at-asc">Date (Oldest)</MenuItem>
                  <MenuItem value="rating-desc">Rating (High)</MenuItem>
                  <MenuItem value="rating-asc">Rating (Low)</MenuItem>
                  <MenuItem value="full_name-asc">Name (A-Z)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Card>

        <Card sx={{ border: '1px solid #e8ecf1', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
          {loading ? (
            <Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead sx={{ backgroundColor: '#f8fafb', borderBottom: '2px solid #e8ecf1' }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Campaign</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Email / Phone</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Rating</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Review</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 600, textAlign: 'center' }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {ratings.length > 0 ? ratings.map((row) => (
                      <TableRow key={row.id} sx={{ '&:hover': { backgroundColor: '#f8fafb' } }}>
                        <TableCell sx={{ fontWeight: 500, maxWidth: 180 }}>
                          {row.campaign_title || `#${row.campaign_id}`}
                        </TableCell>
                        <TableCell>{row.full_name}</TableCell>
                        <TableCell sx={{ color: '#667085' }}>
                          <div>{row.email}</div>
                          <div>{row.phone}</div>
                        </TableCell>
                        <TableCell>
                          <Rating value={Number(row.rating) || 0} readOnly size="small" />
                        </TableCell>
                        <TableCell sx={{ maxWidth: 280, color: '#667085' }}>
                          {(row.review || '').length > 120 ? `${row.review.slice(0, 120)}…` : row.review}
                        </TableCell>
                        <TableCell>
                          <Chip label={row.status} color={getStatusColor(row.status)} size="small" />
                        </TableCell>
                        <TableCell sx={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                          <Button size="small" startIcon={<Edit />} onClick={() => handleStatusChange(row)} sx={{ color: '#2c5f2d' }}>
                            Review
                          </Button>
                          <Button size="small" startIcon={<Delete />} onClick={() => handleDelete(row)} sx={{ color: '#c00' }}>
                            Delete
                          </Button>
                        </TableCell>
                      </TableRow>
                    )) : (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 4, color: '#667085' }}>
                          No ratings found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box sx={{ p: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#f9fafb', borderTop: '1px solid #e8ecf1' }}>
                <Typography variant="body2" sx={{ color: '#667085' }}>
                  Showing {ratings.length > 0 ? (pageNumber - 1) * pagination.itemsPerPage + 1 : 0} to {Math.min(pageNumber * pagination.itemsPerPage, pagination.totalItems)} of {pagination.totalItems}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Button variant="outlined" size="small" disabled={pageNumber === 1} onClick={() => setPageNumber(pageNumber - 1)}>Previous</Button>
                  {[...Array(Math.min(pageButtons, 12))].map((_, i) => (
                    <Button
                      key={i + 1}
                      variant={pageNumber === i + 1 ? 'contained' : 'outlined'}
                      size="small"
                      onClick={() => setPageNumber(i + 1)}
                      sx={{ minWidth: 36, backgroundColor: pageNumber === i + 1 ? '#059669' : 'transparent', color: pageNumber === i + 1 ? 'white' : '#667085' }}
                    >
                      {i + 1}
                    </Button>
                  ))}
                  <Button variant="outlined" size="small" disabled={pageNumber >= pageButtons} onClick={() => setPageNumber(pageNumber + 1)}>Next</Button>
                </Box>
              </Box>
            </>
          )}
        </Card>

        <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ fontWeight: 600 }}>Review rating</DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            {selected && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ color: '#667085' }}>{selected.campaign_title}</Typography>
                <Typography sx={{ fontWeight: 700 }}>{selected.full_name}</Typography>
                <Rating value={Number(selected.rating) || 0} readOnly sx={{ my: 1 }} />
                <Typography sx={{ fontStyle: 'italic' }}>“{selected.review}”</Typography>
              </Box>
            )}
            <TextField
              fullWidth
              select
              label="Status"
              value={newStatus}
              onChange={(e) => setNewStatus(e.target.value)}
              SelectProps={{ native: true }}
              sx={{ mt: 1, mb: 2 }}
            >
              <option value="pending">Pending</option>
              <option value="approved">Approved (show on campaign page)</option>
              <option value="rejected">Rejected</option>
            </TextField>
            <TextField
              fullWidth
              label="Admin notes (optional)"
              value={adminNotes}
              onChange={(e) => setAdminNotes(e.target.value)}
              multiline
              rows={2}
            />
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setOpenDialog(false)} disabled={saving}>Cancel</Button>
            <Button onClick={handleSaveStatus} variant="contained" disabled={saving} sx={{ backgroundColor: '#2c5f2d' }}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar open={toast.open} autoHideDuration={6000} onClose={() => setToast({ ...toast, open: false })} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
          <Alert onClose={() => setToast({ ...toast, open: false })} severity={toast.type} sx={{ width: '100%' }}>
            {toast.message}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default CampaignRatingsPage;
